import { html } from "htm/preact";
import { suggestNextWolf } from "../../../shared/formats/wolf.js";
import { getFormat } from "../../../shared/formats/index.js";
import type { Course, PerHoleData, Player, Round, Score } from "../../../shared/types.js";

interface WolfConfig {
  rotationStrategy?: "rotate" | "trailing" | "manual";
}

export function WolfPanel({
  round,
  course,
  players,
  scores,
  perHoleData,
  hole,
  holeIndex,
  totalHoles,
  disabled = false,
  onSet,
}: {
  round: Round;
  course: Course;
  players: Player[];
  scores: Score[];
  perHoleData: PerHoleData[];
  hole: number;
  holeIndex: number;
  totalHoles: number;
  /** A closed round refuses these on the server too — offering them anyway meant a tap
   * that appeared to take, then silently snapped back when the rejection arrived. */
  disabled?: boolean;
  onSet: (data: { wolfId: string; partnerId: string | null; blind: boolean }) => void;
}) {
  const teeSet = course.teeSets.find((t) => t.name === round.teeSet);
  const existing = perHoleData.find((d) => d.roundId === round.id && d.hole === hole && d.key === "wolf");
  const wolfId = (existing?.data.wolfId as string) ?? null;
  const partnerId = (existing?.data.partnerId as string | null | undefined) ?? null;
  const blind = Boolean(existing?.data.blind);

  let suggestion: string | null = null;
  if (!wolfId && teeSet) {
    const config = (round.formatConfig ?? {}) as WolfConfig;
    const order = players.map((p) => p.id);
    const perHole = perHoleData
      .filter((d) => d.roundId === round.id && d.key === "wolf")
      .map((d) => ({ hole: d.hole, ...d.data }));
    const currentPoints = getFormat("wolf").score({ round, course: teeSet, players, scores, perHole }).points;
    suggestion = suggestNextWolf(order, config.rotationStrategy ?? "trailing", totalHoles, holeIndex, currentPoints);
  }

  const activeWolf = wolfId ?? suggestion;

  return html`
    <div class="wolf-panel">
      <strong>Wolf on hole ${hole}</strong>
      <p class="muted">
        ${activeWolf ? `${players.find((p) => p.id === activeWolf)?.name ?? activeWolf} is the Wolf` : "Pick the Wolf"}
      </p>
      <div class="wolf-choices">
        ${players.map(
          (p) => html`
            <button
              key=${p.id}
              class=${activeWolf === p.id ? "" : "secondary"}
              disabled=${disabled}
              onClick=${() => onSet({ wolfId: p.id, partnerId: null, blind: false })}
            >
              ${p.name}
            </button>
          `,
        )}
      </div>

      ${activeWolf &&
      html`
        <p class="muted mt-3">Partner (or Lone Wolf):</p>
        <div class="wolf-choices">
          ${players
            .filter((p) => p.id !== activeWolf)
            .map(
              (p) => html`
                <button
                  key=${p.id}
                  class=${partnerId === p.id ? "" : "secondary"}
                  disabled=${disabled}
                  onClick=${() => onSet({ wolfId: activeWolf!, partnerId: p.id, blind })}
                >
                  ${p.name}
                </button>
              `,
            )}
          <button
            class=${partnerId === null && wolfId ? "" : "secondary"}
            disabled=${disabled}
            onClick=${() => onSet({ wolfId: activeWolf!, partnerId: null, blind })}
          >
            Lone Wolf
          </button>
        </div>
        <label class="row mt-2">
          <input
            type="checkbox"
            class="checkbox"
            checked=${blind}
            disabled=${disabled}
            onChange=${(e: any) => onSet({ wolfId: activeWolf!, partnerId, blind: e.currentTarget.checked })}
          />
          Blind Wolf
        </label>
      `}
    </div>
  `;
}
